import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useEffect, useRef } from 'react';
import BookingsButton from '../BookingsButton';
import CalendarOption from '../CalendarOption';
import { useAppSelector } from '../../../redux/hooks';
import {
  VideoCallPlatform,
  checkVideoPlatformTokenExists,
  checkVideoPlatformTokenExistsSuccess,
  deleteVideoPlatform,
  getGoogleMeetAuthURI,
  getZoomAuthURI,
  resetCheckVideoPlatformTokenExists,
  resetDeleteVideoPlatform,
  resetSaveLinkedVideoCallPlatform,
  resetVideoPlatforAuthURI,
  saveLinkedVideoPlatform,
} from '../../../redux/booking/bookingSlice';
import { TokenTypeEnum } from '../../../types/booking';

const videoCallPlatformOptions = [
  {
    id: 1,
    icon: '/assets/icons/logos-google-meet.svg',
    disableIcon: '/assets/icons/google-meet-faded.svg',
    text: 'Google Meet',
  },
  // {
  //   id: 2,
  //   icon: '/assets/icons/zoom-icon.svg',
  //   disableIcon: '/assets/icons/logos-zoom.svg',
  //   text: 'Zoom',
  // },
];

type IOnlineConsultationsStepProps = {
  isProvideOnlineConsultations: boolean | null;
  setIsProvideOnlineConsultations: (_isProvideOnlineConsultations: boolean | null) => void;
  setSelectedOption: (_optionId: number | null) => void;
  selectedOption: number | null;
  hasPermissions: boolean;
};

function OnlineConsultationsStep({
  isProvideOnlineConsultations,
  setIsProvideOnlineConsultations,
  selectedOption,
  setSelectedOption,
  hasPermissions,
}: Readonly<IOnlineConsultationsStepProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const {
    checkVideoPlatformTokenExistsLoading,
    coachConfigurations,
    googleMeetAuthURI,
    deleteVideoPlatformLoading,
    deleteVideoPlatformSuccess,
    videoPlatformUsername,
    // zoomAuthURI,
    zoomAuthURIFailure,
    googleMeetAuthURIFailure,
    checkVideoPlatformTokenExistsFailure,
    googleMeetAuthURISuccess,
    isVideoPlatformTokenExists,
    zoomAuthURISuccess,
    linkedVideoCallPlatformSuccess,
    linkedVideoCallPlatformLoading,
  } = useAppSelector((state) => state.booking);
  const authWindowRef = useRef<Window | null>(null);

  useEffect(() => {
    if (zoomAuthURIFailure || googleMeetAuthURIFailure || checkVideoPlatformTokenExistsFailure) {
      setSelectedOption(null);
    }
  }, [zoomAuthURIFailure, googleMeetAuthURIFailure, checkVideoPlatformTokenExistsFailure]);

  useEffect(() => {
    if (coachConfigurations.videoCallPlatform === VideoCallPlatform.GOOGLE_MEET) {
      setSelectedOption(1);
      dispatch(checkVideoPlatformTokenExistsSuccess({ isTokenExist: true }));
    }
    if (coachConfigurations.videoCallPlatform === VideoCallPlatform.ZOOM) {
      setSelectedOption(2);
      dispatch(checkVideoPlatformTokenExistsSuccess({ isTokenExist: true }));
    }
  }, [coachConfigurations.videoCallPlatform]);

  useEffect(() => {
    if (googleMeetAuthURI && !authWindowRef.current) {
      authWindowRef.current = window.open(
        googleMeetAuthURI,
        '_blank',
        'location=yes,height=570,width=520,scrollbars=yes,status=yes'
      );

      const checkWindowClosed = setInterval(() => {
        if (authWindowRef.current?.closed) {
          clearInterval(checkWindowClosed);
          authWindowRef.current = null;
          dispatch(
            checkVideoPlatformTokenExists({ coachId, tokenType: TokenTypeEnum.GOOGLE_CALENDER })
          );
        }
      }, 1000);

      return () => {
        clearInterval(checkWindowClosed);
        if (authWindowRef.current) {
          authWindowRef.current.close();
          authWindowRef.current = null;
        }
      };
    }
  }, [googleMeetAuthURI]);

  // useEffect(() => {
  //   if (zoomAuthURI) {
  //     const authWindow = window.open(
  //       zoomAuthURI,
  //       '_blank',
  //       'location=yes,height=570,width=520,scrollbars=yes,status=yes'
  //     );

  //     const checkWindowClosed = setInterval(() => {
  //       if (authWindow?.closed) {
  //         clearInterval(checkWindowClosed);
  //         dispatch(checkVideoPlatformTokenExists({ coachId, tokenType: TokenTypeEnum.ZOOM }));
  //         dispatch(resetVideoPlatforAuthURI());
  //       }
  //     }, 1000); // Check every second

  //     return () => clearInterval(checkWindowClosed); // Cleanup interval on component unmount
  //   }
  // }, [zoomAuthURI]);

  useEffect(() => {
    if (googleMeetAuthURISuccess && isVideoPlatformTokenExists && videoPlatformUsername) {
      if (selectedOption === 1) {
        dispatch(
          saveLinkedVideoPlatform({
            coachId,
            videoCallPlatform: VideoCallPlatform.GOOGLE_MEET,
            videoCallPlatformUsername: videoPlatformUsername,
          })
        );
      }
    }
    if (zoomAuthURISuccess && isVideoPlatformTokenExists && videoPlatformUsername) {
      if (selectedOption === 2) {
        dispatch(
          saveLinkedVideoPlatform({
            coachId,
            videoCallPlatform: VideoCallPlatform.ZOOM,
            videoCallPlatformUsername: videoPlatformUsername,
          })
        );
      }
    }
  }, [
    googleMeetAuthURISuccess,
    isVideoPlatformTokenExists,
    zoomAuthURISuccess,
    videoPlatformUsername,
  ]);

  useEffect(() => {
    if (linkedVideoCallPlatformSuccess) {
      dispatch(resetSaveLinkedVideoCallPlatform());
      dispatch(resetVideoPlatforAuthURI());
    }
  }, [linkedVideoCallPlatformSuccess]);

  const handleSelectVideoOption = (optionId: number | null) => {
    setSelectedOption(optionId);
    if (optionId === 1) {
      dispatch(
        getGoogleMeetAuthURI({
          coachId,
          calendar: coachConfigurations.calendar,
          calendarUsername: coachConfigurations.calendarUsername,
          activeStep: 3,
        })
      );
    }
    if (optionId === 2) {
      dispatch(getZoomAuthURI());
    }
    dispatch(resetDeleteVideoPlatform());
    dispatch(resetSaveLinkedVideoCallPlatform());
  };

  const handleDisableOption = () => {
    setIsProvideOnlineConsultations(false);
    if (isVideoPlatformTokenExists && selectedOption) {
      dispatch(
        deleteVideoPlatform({
          calenderType: selectedOption === 1 ? TokenTypeEnum.GOOGLE_CALENDER : TokenTypeEnum.ZOOM,
          isConfigured: true,
        })
      );
    }
    setSelectedOption(null);
  };

  useEffect(
    () => () => {
      dispatch(resetSaveLinkedVideoCallPlatform());
      dispatch(resetCheckVideoPlatformTokenExists());
      dispatch(resetVideoPlatforAuthURI());
      setSelectedOption(null);
    },
    [dispatch]
  );

  return (
    <Grid display="flex" flexDirection="column" alignItems="center" height="calc(100% - 68px)">
      <Grid gap={4} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="title1Bold" color="primary">
          {t('bookings.onlineConsultationsStep.title')}
        </Typography>
        {isProvideOnlineConsultations && (
          <Grid display="flex" flexDirection="row" gap="20px">
            <BookingsButton
              onClick={() => setIsProvideOnlineConsultations(true)}
              isSelected={isProvideOnlineConsultations}
              disabled={!hasPermissions}
            >
              {t('bookings.onlineConsultationsStep.yes')}
            </BookingsButton>
            <BookingsButton
              paddingX={93}
              onClick={handleDisableOption}
              isSelected={!isProvideOnlineConsultations}
              disabled={!hasPermissions}
            >
              {t('bookings.onlineConsultationsStep.no')}
            </BookingsButton>
          </Grid>
        )}
      </Grid>
      {!isProvideOnlineConsultations && (
        <Grid
          display="flex"
          justifyContent="center"
          m="auto"
          gap="20px"
          width="100%"
          flexDirection="row"
          alignItems="center"
        >
          <BookingsButton
            onClick={() => {
              setIsProvideOnlineConsultations(true);
              setSelectedOption(null);
            }}
            isSelected={isProvideOnlineConsultations}
            disabled={!hasPermissions}
          >
            {t('bookings.onlineConsultationsStep.yes')}
          </BookingsButton>
          <BookingsButton
            paddingX={93}
            onClick={handleDisableOption}
            isSelected={isProvideOnlineConsultations !== null && !isProvideOnlineConsultations}
            disabled={!hasPermissions}
          >
            {t('bookings.onlineConsultationsStep.no')}
          </BookingsButton>
        </Grid>
      )}
      {isProvideOnlineConsultations && (
        <Grid
          display="flex"
          justifyContent="center"
          m="auto"
          gap={4}
          width="100%"
          flexDirection="column"
          alignItems="center"
        >
          <Typography variant="h6" color="primary">
            {t('bookings.onlineConsultationsStep.configure')}
          </Typography>
          <Grid display="flex" flexDirection="column" gap={2} width="100%" alignItems="center">
            {videoCallPlatformOptions.map((option) => (
              <CalendarOption
                key={option.id}
                icon={option.icon}
                text={option.text}
                id={option.id}
                disableIcon={option.disableIcon}
                selectedOption={selectedOption}
                onSelect={handleSelectVideoOption}
                loading={checkVideoPlatformTokenExistsLoading || linkedVideoCallPlatformLoading}
                deleteLoading={deleteVideoPlatformLoading}
                deleteSuccess={deleteVideoPlatformSuccess}
                email={videoPlatformUsername ?? coachConfigurations.videoCallPlatformUsername}
                isCalendar={false}
                hasPermissions={hasPermissions}
              />
            ))}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}

export default OnlineConsultationsStep;
